import { useCallback, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Divider } from '@mui/material';
import { useForm } from 'react-hook-form-mui';
import Snackbar from 'src/presentations/components/atom/snackbar/Snackbar';

import { beautifyEnum } from 'src/utils/utility';
import { useCreateUserIssue } from 'src/use-cases/user/useUserIssue';
import IconTick from 'src/assets/images/icons/tick-circle-gray.svg';
import Modal from '../modal';
import { useContactSelector } from './ContactSelector';
import TransportMediumIcon from '../card/TransportMediumIcon';
import { TransportMedium } from '../card/types';
import StatusChip from '../../organisms/dashboard/StatusChip';
import { FormContainer } from '../../organisms/forms/FormContainer';
import { ModalContactForm } from './ModalContactForm';
import { IContactFormValues } from './types';

export const StyledModal = styled(Modal)({
  zIndex: 9999,

  '& .MuiDialog-paper': {
    maxWidth: '680px',
    borderRadius: '16px',
  },
  '& .MuiDialogContent-root': {
    padding: 0,
  },
});

export const ModalContact = () => {
  const { isShowContact, invoice, issue, setIsShowContact, setInvoice, setIssue } = useContactSelector();
  const { mutateAsync, isLoading } = useCreateUserIssue();

  const [showNotification, setShowNotification] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const formContext = useForm({
    defaultValues: {
      message: '',
    },
  });

  const onCloseModal = useCallback(() => {
    setIsShowContact(false);
    setInvoice(null);
  }, [setInvoice, setIsShowContact]);

  const onSubmit = useCallback(
    async (values: IContactFormValues) => {
      try {
        const response = await mutateAsync({
          invoiceId: invoice?.id,
          message: values?.message,
          shipmentInvoiceId: invoice?.shipmentInvoiceId,
        });

        if (response?.data?.success) {
          const userIssue = response?.data?.data;
          const issueData = {
            invoiceNumber: invoice?.invoiceNumber,
            // Preserve invoice data for notification
            totalShipments: invoice?.totalShipments,
            shipmentNumber: invoice?.shipmentNumber,
            shipmentInvoiceId: invoice?.shipmentInvoiceId,
            ...userIssue,
          };

          setIsShowContact(false);
          setInvoice(null);
          setIssue(issueData);
          setShowNotification(true); // Show notification when issue is set
        }
      } catch {
        setIsShowContact(false);
        setInvoice(null);
      }
    },
    [invoice, mutateAsync, setInvoice, setIsShowContact, setIssue]
  );

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (showNotification) {
      // Set a timeout to start the exit animation
      const timer = setTimeout(() => {
        setIsExiting(true);
      }, 4500);

      // Set another timeout to fully hide the notification
      const hideTimer = setTimeout(() => {
        setShowNotification(false);
        setIsExiting(false);
      }, 5000);

      return () => {
        clearTimeout(timer);
        clearTimeout(hideTimer);
      };
    }
  }, [showNotification]);

  return (
    <>
      <StyledModal open={isShowContact} title='Contact Freya' fullWidth onClose={onCloseModal}>
        <div className='relative pt-10 pb-24 px-24'>
          <div className='bg-neutral-10 rounded-10 p-16'>
            <p className='text-16 font-medium pb-16 mb-16 border-b border-neutral-20'>Problem with the Invoice data:</p>

            {invoice && (
              <>
                <div className='flex items-center gap-10'>
                  <div className='flex items-center justify-center'>
                    <TransportMediumIcon medium={invoice?.modality as TransportMedium} iconColor='#9F9F9F' size='md' />
                  </div>
                  <h2 className='text-16 font-bold text-blue_grey-80'>{beautifyEnum(invoice?.provider)?.toUpperCase()}</h2>
                  <div className='w-4 h-4 rounded-full bg-[#D9D9D9]' />
                  <div className='uppercase text-neutral-60 text-14'>{invoice?.modality}</div>
                </div>

                <div className='flex items-center gap-16 mt-6'>
                  <p className='flex items-center gap-4 text-14'>
                    Invoice No.
                    <strong className='text-16 font-bold text-primary'>{invoice?.invoiceNumber}</strong>
                  </p>
                  <Divider className='bg-neutral-20' orientation='vertical' variant='middle' flexItem />
                  <p className='text-16 font-bold text-primary'>{invoice?.mblHbl}</p>
                  <Divider className='bg-neutral-20' orientation='vertical' variant='middle' flexItem />
                  <StatusChip status={invoice?.status} size='small' />
                </div>
              </>
            )}
          </div>

          <FormContainer context={formContext} onSuccess={onSubmit}>
            <ModalContactForm isLoading={isLoading} />
          </FormContainer>
        </div>
      </StyledModal>

      {showNotification && issue && (
        <div className={isExiting ? 'animate-slide-out-down' : 'animate-slide-in-up'}>
          <Snackbar
            position='bottom-left'
            zIndex={20}
            autoSidebarPlacement
            icon={<img src={IconTick} alt='' />}
            content={
              <div className='relative'>
                <p>
                  Shipment <strong className='px-6 text-14 inline-block font-bold text-primary'>{issue?.shipmentNumber}</strong> is now under review.
                </p>
                <p className='mt-4 text-12 text-neutral-60'>
                  The Support Team will be in touch with you shortly. <br />
                  Issue {issue?.issueId} has been created.
                </p>
              </div>
            }
          />
        </div>
      )}
    </>
  );
};

export default ModalContact;
